import { BookOpen, Plus, Trash2, Wrench } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import CioChat from '@/components/cio/CioChat'
import { useCioChat } from '@/components/cio/useCioChat'

/**
 * AI CIO — the full page: conversation history on the left, chat on the right.
 *
 * The chat itself is `CioChat`, shared with the docked pane in `CioDock`, so the two are
 * the same conversation component rather than two implementations that drift apart. What
 * the page adds is room: the thread list, the tool inventory, and the suggestion chips
 * that do not fit in a narrow pane.
 */

const btn = 'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm'
const btnPrimary = `${btn} bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50`

export default function AiCio() {
  const chat = useCioChat()
  const tools = useQuery({
    queryKey: ['cio-tools'],
    queryFn: () => api.get('/ai-cio/tools').then(r => r.data),
    staleTime: Infinity,
  })

  const st = chat.status

  return (
    <div className="flex h-[calc(100vh-7rem)] gap-4 p-1">
      {/* ------------------------------------------------------------ sidebar */}
      <aside className="hidden w-60 flex-none flex-col lg:flex">
        <button className={`${btnPrimary} mb-3 justify-center`} onClick={chat.reset}>
          <Plus size={15} />New conversation
        </button>
        <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-gray-200 bg-white">
          {chat.conversations.length === 0 && (
            <div className="p-3 text-xs text-gray-500">No conversations yet.</div>
          )}
          {chat.conversations.map((c: any) => (
            <div key={c.id}
                 className={`group flex items-center gap-1 border-b border-gray-100 px-3 py-2 text-xs last:border-0 ${
                   c.id === chat.convId ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
              <button className="min-w-0 flex-1 truncate text-left text-gray-800"
                      title={c.title} onClick={() => chat.open(c.id)}>
                {c.title || 'Untitled'}
                <span className="ml-1 text-gray-400">· {c.n_messages}</span>
              </button>
              <button className="invisible text-gray-400 hover:text-red-600 group-hover:visible"
                      title="Delete"
                      onClick={() => { if (confirm('Delete this conversation?')) chat.remove(c.id) }}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>

        {st && (
          <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-600">
            <div className="mb-1 flex items-center gap-1.5 font-medium text-gray-700">
              <Wrench size={13} />{st.n_tools} tools
            </div>
            <div className="text-gray-500">{(st.groups || []).join(' · ')}</div>
            <div className="mt-1.5 flex items-center gap-1.5 text-gray-500">
              <BookOpen size={12} />KB {st.kb_version}
            </div>
            {st.model && <div className="mt-1 truncate text-gray-400">{st.model}</div>}
          </div>
        )}
      </aside>

      {/* --------------------------------------------------------------- chat */}
      <main className="flex min-w-0 flex-1 flex-col">
        <div className="mb-3">
          <h1 className="text-2xl font-semibold text-gray-900">AI CIO</h1>
          <p className="text-sm text-gray-500">
            Answers from Plutus's own tools, and tells you what each number rests on.
            Available on any page from the button at the bottom right, or Ctrl J.
          </p>
        </div>

        <CioChat
          msgs={chat.msgs}
          busy={chat.busy}
          activeTools={chat.activeTools}
          status={st}
          unavailable={chat.unavailable}
          onSend={chat.send}
          onStop={chat.stop}
        />

        {!!tools.data?.tools?.length && (
          <div className="mt-1.5 text-[11px] text-gray-400">
            {tools.data.tools.length} tools across {tools.data.groups.join(', ')}. Every
            answer is produced by calling them — nothing is answered from memory.
          </div>
        )}
      </main>
    </div>
  )
}
